import styled from "styled-components";

import { ReactComponent as WarningIcon } from "../assets/warning.svg";
import Card from "./Cards/Card";
import { Image } from "../hooks/useImageUploader";
import {
  IFormValues,
  InspectionInfoBody,
  Step,
  StepType,
} from "./InspectionForm";
import { isStepValid } from "../utils";

const InspectionFormReviewContainer = (props: {
  steps: Step[];
  onStep: Function;
  images: Image[];
  answers: IFormValues;
  notes: { [key: string]: string };
  inspectionCompleted?: boolean;
}) => {
  return (
    <InspectionInfoBody>
      <Summary>
        {props.steps?.map((step, ix) => (
          <Row
            key={step.key}
            onClick={
              !props.inspectionCompleted ? () => props.onStep(ix) : undefined
            }
          >
            <QuestionContainer>
              <Question>{step.title}</Question>
              {!isStepValid(step, props.answers[step.key], props.images) ? (
                <ErrorMessage>
                  <Warning fill="#F92B2B" width={16.47} height={16.47} />
                  Je hebt deze vraag nog niet ingevuld
                </ErrorMessage>
              ) : (
                formatAnswer(step, props.answers[step.key])
              )}
              {step?.type === StepType?.photo ? (
                <PhotoOverview
                  step={step}
                  images={props.images?.filter(
                    ({ metadata }) => metadata?.step === step.key
                  )}
                />
              ) : null}
            </QuestionContainer>
            <OptionsContainer>
              {props.notes[step.key] ? (
                <Option>
                  <OptionTitle>
                    {step.key === "approval"
                      ? "Advies bij eindoordeel"
                      : "Opmerking"}
                  </OptionTitle>
                  <OptionNote>{props.notes[step.key]}</OptionNote>
                </Option>
              ) : null}
              {step.type !== StepType.photo &&
              props.images?.filter(
                ({ metadata }) => metadata?.step === step.key
              )?.length ? (
                <Option>
                  <PhotoOverview
                    step={step}
                    images={props.images?.filter(
                      ({ metadata }) => metadata?.step === step.key
                    )}
                  />
                </Option>
              ) : null}
            </OptionsContainer>
          </Row>
        ))}
      </Summary>
    </InspectionInfoBody>
  );
};

const PhotoOverview = ({ step, images }: { step: Step; images: Image[] }) =>
  images?.length ? (
    <Images>
      <ImagesTitle>Foto's</ImagesTitle>
      {images
        ?.filter(({ metadata }) => metadata?.step === step.key)
        ?.map((image) => (
          <CardPreview key={`${image.key}_preview`}>
            <img
              src={image.path || image.thumbnailUrl || image.downloadUrl}
              alt={image.key}
              title={image.metadata?.description}
              width={55}
              height={40}
              onError={(e) =>
                image.downloadUrl && (e.currentTarget.src = image.downloadUrl)
              }
            />
          </CardPreview>
        ))}
    </Images>
  ) : null;

function formatAnswer(step: Step, answer: any) {
  switch (step.type) {
    case StepType.open:
    case StepType.radio:
      return <Answer>{answer}</Answer>;
    case StepType.closed:
      return <Answer>{answer}</Answer>;
    case StepType.checklist:
    case StepType.multipleChoice:
      const isCountable =
        Object.values(answer || {}).filter(
          (answer) => typeof answer === "number"
        ).length === Object.values(answer || {}).length;
      return (
        <Summary>
          {isCountable
            ? step.choices
                ?.filter(
                  (_, index) => typeof (answer || {})[`c${index}`] === "number"
                )
                .map(
                  (choice: string, index: number) =>
                    answer &&
                    answer[`c${index}`] > 0 && (
                      <Answer key={`countable_${index}`}>
                        {choice}: {answer[`c${index}`]}
                      </Answer>
                    )
                )
            : step.choices
                ?.filter((_, index) => !!(answer || {})[`c${index}`])
                .map((choice: string) => (
                  <Answer key={`choice_${choice}`}>{choice}</Answer>
                ))}
        </Summary>
      );
    default:
      return true;
  }
}

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
  border-bottom: 1px solid #e5e5e5;
  padding-top: 14px;
  padding-bottom: 14px;
  &:last-of-type {
    border-bottom: unset;
  }
`;

const Question = styled.span`
  /* Tablet/Label regular */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 120%;

  /* or 16px */
  display: flex;
  align-items: center;

  /* Donkergrijs */
  color: #212529;
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.01);
  }
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const Answer = styled(Question)`
  display: block;
  font-weight: 700;
  text-align: right;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-end;
  :empty {
    display: none;
  }
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
  font-size: 10px;
  font-family: "Roboto";
  background: #f7f7f7;
  cursor: pointer;
`;

const OptionTitle = styled.span`
  font-weight: 700;
  font-family: "Arial";
  padding: 10px 22px;
  padding-bottom: 0px;
`;

const ImagesTitle = styled.span`
  font-weight: 700;
  flex-basis: 100%;
  font-family: "Arial";
`;

const OptionNote = styled.span`
  padding: 10px 22px;
`;

const ErrorMessage = styled.span`
  font-family: "Roboto";
  font-style: italic;
  font-weight: 400;
  font-size: 10px;
  line-height: 120%;

  /* or 12px */
  display: flex;
  align-items: center;

  /* Donkergrijs */
  color: #212529;
`;

const Warning = styled(WarningIcon)`
  padding-right: 8px;
`;

const Images = styled.div`
  display: flex;
  flex-basis: 100%;
  flex-direction: row;
  padding: 10px 22px;
  background: #f7f7f7;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 10px;
  font-family: "Roboto";
  background: #f7f7f7;
  cursor: auto;
`;

const CardPreview = styled(Card)`
  width: 55px;
  height: 40px;
  overflow: hidden;
`;

export default InspectionFormReviewContainer;
